/**
 * 2102-zg-getProductInfo.js
 *
 * Requests the information for products and display the result retrieved.
 *
 * PATCH NOTES (infinite scroll):
 * 1. Old code computed offset()/scrollTop on every scroll event and used
 *    the FOOTER as the trigger element: the AJAX call only fired once the
 *    footer became visible, causing a visible pause before new products
 *    appeared.
 * 2. New behaviour: no distance-to-bottom threshold at all. As soon as the
 *    user scrolls (mouse wheel, trackpad, touch, scrollbar - any scroll or
 *    wheel event) and there is no request already in flight, the next
 *    page is requested immediately, regardless of how far the user is
 *    from the end of the currently loaded products. Once that request
 *    completes, the very next scroll event fires the following page, and
 *    so on. This keeps loading "one step ahead" continuously while the
 *    user is actively scrolling, instead of waiting to get close to the
 *    bottom.
 * 3. The full page loader (#page-loader) is only shown for the very first
 *    load of the plugin (or for non-infinite-scroll actions, such as
 *    clicking pages/pager/load-more, changing filters/sort, etc). Once
 *    infinite scroll starts loading subsequent pages triggered by user
 *    scrolling, the full page loader is no longer used; a small inline
 *    loader is shown instead, only if the optional element
 *    [data-zg-role="inline-loader"] exists in the markup (selector option
 *    inlineLoaderSelector) - if it does not exist, nothing is shown and
 *    nothing breaks.
 *
 *    FIX: previously __onComplete/__onError decided which loader to hide
 *    based only on `paginationType === 'infinite'`, while __onBeforeSend
 *    decided which loader to SHOW based on `paginationType === 'infinite'
 *    && _isInfiniteRequestPending`. Since paginationType stays 'infinite'
 *    for every request (including the first one), __onComplete always
 *    tried to hide the inline loader, even when the full page loader had
 *    been the one shown - so #page-loader from the first load was never
 *    closed. Now a single flag (`_usingFullPageLoader`), set in
 *    __onBeforeSend, is reused in __onComplete/__onError to close the
 *    correct loader.
 *
 * Integration:
 * - Replace the original file with this one. No new CSS or handlebars
 *   templates are required.
 *
 * @author: Dario Lopez <dlopez[at]kooomo.com>
 */


(function ( $, _ ) {
    'use strict';

    // Establish the root object ('window' in the browser)
    var root = this;

    /**
     * @selector data-zg-role="get-product-bck" The plugin start if there is the selector in the dom when the page load
     */
    var SELECTOR = '[data-zg-role="get-product-bck"]';

    // url for the AJAX request
    var REQUEST_URL = root.makeUrl( { module: 'eshop', manager: 'eshop', action: 'getProductsInfo' } );

    var DEFAULT_REQUEST = {
        categoryId: null,
        products: null,
        forceResult: false,
        getCategories: true,
        getCharacteristics: false,
        getClassification: false,
        getCustomValues: false,
        getDescriptions: false,
        getImages: false,
        getImages360: false,
        getLinkedProducts: false,
        getNextPrev: false,
        getOptions: false,
        getQuantity: false,
        getPrice: false,
        getPricePerCountry: false,
        getPromotions: false,
        getPhysicalCharacteristics: false,
        getProperties: false,
        getSeo: false,
        getSkus: false,
        getStandardOptions: false,
        getTextile: false,
        getUrl: false,
        getFilterCategories: false,
        getFilterCharacteristics: false,
        getFilterClassification: false,
        getFilterOptions: false,
        getFilterProperties: false,
        getFilterPrice: false,
        getFilterPromotions: false,
        pageSize: 5,
        page: 0
    };


    var DEFAULTS = {
        type: 'product',
        namespace: null,
        productsTemplate: null,
        errorTemplate: 'product-error',
        productsContainer: null,

        paginationXYelements:  '[data-zg-role="x-y-elements"]',
        paginationNumberPages: '[data-zg-role="pagination-number-pages"]',
        paginationGoTo:        '[data-zg-role="pagination-go-to-page"]',
        paginationControls:    '[data-zg-role="pagination-controls"]',
        pagerControls:         '[data-zg-role="pager-controls"]',
        pagerPrev:             '#pager-prev',
        pagerNext:             '#pager-next',
        paginationCurrent:     '[data-zg-role="pagination-current"]',
        elementControlScroller:'#site_footer',
        paginationLoadMoreBtn: '#pagination-load-more',
        paginationNext:        '#pagination-next',
        paginationPrev:        '#pagination-prev',
        templateFilterList:   'filter-list',
        templateFilterReset:  'filter-reset',
        templateFilterSearch: 'filter-search',
        templateFilterSlider: 'filter-slider',
        isFirstLoad:    true,
        emptyContainer: true,
        filterElement:  '[data-zg-action]',
        resetFilters:   '[data-zg-role="reset-filters"]',
        validParameters:'[data-valid-parameters]',
        enableReset:    true,
        enableSearch:   false,
        searchButton:   '[data-zg-role="filter-search-btn"]',
        searchInFields: null,
        searchTypeaheadFields: false,
        sliderMinDiff:  50,
        sliderStep:     10,
        sliderMin:      0,
        sliderMax:      0,
        sortFilters:    true,
        sortFiltersBy:  'id',
        elementSorting: '[data-zg-role="pagination-sort-bck"]',
        elementCounter: '[data-zg-role="pagination-counter"]',
        paginationType: 'pages',
        updateUrlByPage: false,
        total:          0,
        totalPages:     0,

        // >>> PATCH: infinite scroll "eager" - nessuna soglia di distanza,
        // si carica la pagina successiva ad ogni scroll dell'utente
        inlineLoaderSelector: '[data-zg-role="inline-loader"]' // indicatore leggero opzionale, mostrato solo se l'elemento esiste in pagina
    };


    // SKELETON-PLUGIN CLASS DEFINITION
    // ===========================

    var GetProductInfo = function ( element, options ) {
        this.$element = $( element );

        this.options = {};
        this.request = {};
        this.updateOptions( options );

        this.$container = this.options.productsContainer ? $( this.options.productsContainer ) : this.$element;

        this.cached = {};

        this.$filterContainer       = $( this.options.filterContainer );

        this.$paginationXYelements  = $( this.options.paginationXYelements );
        this.$paginationNumberPages = $( this.options.paginationNumberPages );
        this.$paginationNext        = $( this.options.paginationNext );
        this.$paginationPrev        = $( this.options.paginationPrev );
        this.$paginationCurrent     = $( this.options.paginationCurrent );
        this.$pagerControls         = $( this.options.pagerControls );
        this.$pagerPrev             = $( this.options.pagerPrev );
        this.$pagerNext             = $( this.options.pagerNext );
        this.$paginationControls    = $( this.options.paginationControls );
        this.$paginationLoadMoreBtn = $( this.options.paginationLoadMoreBtn );
        this.$controlScroller       = $( this.options.elementControlScroller );
        this.$elementSorting        = $( this.options.elementSorting );
        this.$counter               = $( this.options.elementCounter );

        // >>> PATCH: indicatore inline opzionale + flag di richiesta in corso
        this.$inlineLoader          = $( this.options.inlineLoaderSelector );
        this._isInfiniteRequestPending = false;

        // >>> FIX: tiene traccia di quale loader e' stato mostrato per
        // l'ultima richiesta, cosi' __onComplete/__onError possono chiudere
        // esattamente quello (e non basarsi solo su paginationType, che
        // resta 'infinite' anche per il primo caricamento)
        this._usingFullPageLoader = false;

        this.__processFilters();
        this.__setEventHandlers();
    };


    GetProductInfo.prototype.__processFilters = function(){
        var filter = {};
        var data = $.uriMgr( { action: 'getStatus' } ).components;

        _.each( data, function( values, index ){
            if( _.isArray( values, index ) && _.contains( this.options.validParameters, index ) ){

                if( index === 'search' ) {
                    this.request.page = 0;

                    var searched_string = values[0];
                    var searched = {value: searched_string, fields: ['name', 'option', 'mainoption']};

                    this.request.filterBy = {};
                    this.request.filterBy.search = searched;
                }
                else {
                    if( index === 'sortBy' ){
                        this.request.sortBy = values[0];
                    }
                    else {
                        if( index === 'price-max' || index === 'price-min' ){
                            filter[ index ] = values[0];
                        }
                        else if (index === 'page') {
                            this.request.page = values[0];
                            this.page = parseInt(values[0]);

                            if(this.options.isFirstLoad
                                && (this.page > 0)
                                && (this.options.paginationType !== 'pages')
                                && (this.options.paginationType !== 'pager')){
                                this.options.resetPages = true;
                                this.request.pageSize = this.options.pageSize * (this.page + 1);
                                this.request.page = 0;
                            }
                        }
                        else {
                            filter[ index ] = values;
                        }

                        this.request.filterBy = _.extend(this.request.filterBy || {}, filter);
                    }
                }
            }
        }, this);
    };


    GetProductInfo.prototype.getInfo = function ( createFilter, updateUrl ) {
        var cacheKey;

        if ( this.request.categoryId || this.request.products ) {
            cacheKey = JSON.stringify( this.request );

            if ( this.cached[cacheKey] ) {
                this.__onBeforeSend();
                this.cached[cacheKey].createFilter = false;
                this.cached[cacheKey].updateUrl = true;
                this.__onSuccess( this.cached[cacheKey] );
                this.__onComplete();
            } else {
                $.ajax( {
                    type:     'POST',
                    url:      REQUEST_URL,
                    dataType: 'json',
                    data:     this.request,

                    beforeSend: function () {
                        this.__onBeforeSend();
                    }.bind( this ),

                    success: function ( response ) {
                        response.createFilter = createFilter;
                        response.updateUrl = updateUrl;

                        this.__onSuccess( response );
                        this.cached[cacheKey] = response;
                    }.bind( this ),

                    error: function ( response ) {
                        this.__onError( response );
                    }.bind( this ),

                    complete: function () {
                        this.__onComplete();
                    }.bind( this )
                } );
            }
        } else if ( DEBUG ) {
            console.warn( 'GetProductInfo.getInfo - not requested: "categoryId" and "products" missing' );
        }
    };


    GetProductInfo.prototype.__createProducts = function ( products ) {
        this.__emtpyContainer();
        this.__renderProducts( products );
    };

    GetProductInfo.prototype.__emtpyContainer = function() {
        if ( this.options.emptyContainer ) {
            this.$container.find( '[data-zg-role="product"]' ).each( function () {
                var productData = $( this ).data( 'zg.product' );

                if ( productData ) {
                    $( document ).off( '.' + productData.options.namespace );
                }
            } );
            this.$container.empty();
            this.options.emptyContainer = false;
        }
    };


    GetProductInfo.prototype.__renderProducts = function ( products ) {
        _.each( products || [], function ( product, index ) {
            var $item = $( handlebarsTemplates.render( this.options.productsTemplate, product ) );

            this.$container.append( $item );

            setTimeout(
                function () {
                    $item.addClass( 'in' );
                }, index * 20
            );

            if ( DEBUG ) {
                console.log( 'GetProductInfo - renderProducts', product );
            }

            $( document ).trigger( 'zg.getProductInfo.productCreated', [$item, this.options, product] );
        }, this);

        // >>> PATCH: nulla da riarmare esplicitamente: il listener di scroll
        // resta attivo, si limita solo a controllare se c'e' ancora una
        // pagina successiva (vedi __setupInfiniteObserver)
    };


    GetProductInfo.prototype.__onSuccess = function ( response ) {
        if ( DEBUG ) {
            console.log( 'GetProductInfo - SUCCESS', response );
        }

        var updateUrl = response.filters && response.updateUrl;

        if(this.options.updateUrlByPage && response.pagination.page > 0 && this.options.paginationType !== 'infinite'){
            updateUrl = true;
        }

        if ( response.products.length > 0 ) {
            this.__processProducts( response.products );
        }
        else {
            if( this.options.isFirstLoad ) {
                this.request.page = 0;
                this.options.isFirstLoad = false;
                this.getInfo();
            }
        }

        if ( ( response.filters ) && response.createFilter ){
            this.__createFilters( response.filters, (response.products || []), response.url );
        }

        if ( updateUrl ) {
            this.__updateURL( response );
        }

        if ( response.products && this.options.productsTemplate ) {
            if(this.options.resetPages){
                this.page = response.pagination.pageSize/this.options.pageSize - 1;
                this.request.page = this.page;

                this.pageSize = this.options.pageSize;
                this.request.pageSize = this.pageSize;

                this.totalPages = Math.ceil( response.pagination.total / this.pageSize );
                this.total = response.pagination.total;

                this.options.resetPages = false;
            } else {
                this.page = response.pagination.page;
                this.pageSize = response.pagination.pageSize;
                this.total = response.pagination.total;
                this.totalPages = Math.ceil( response.pagination.total / response.pagination.pageSize );
            }

            this.__createProducts( response.products );
            this.updateCounters();
        }

        // >>> PATCH: richiesta infinite scroll conclusa, si puo' accettare la prossima
        this._isInfiniteRequestPending = false;
    }


    GetProductInfo.prototype.__onBeforeSend = function () {
        // >>> FIX: il full-page loader compare solo se NON e' un preload di
        // infinite scroll (primo caricamento, click su pagine/pager/load-more,
        // filtri, sort, ecc). Per l'infinite scroll usiamo l'indicatore inline.
        // Il flag _usingFullPageLoader viene salvato per essere riletto in
        // __onComplete/__onError e chiudere il loader giusto.
        if ( this.options.paginationType === 'infinite' && this._isInfiniteRequestPending ) {
            this._usingFullPageLoader = false;
            this.__showInlineLoader();
        } else {
            this._usingFullPageLoader = true;
            $( '#page-loader' ).css('display', 'flex').hide().fadeIn(200);
        }
        $( document ).trigger( 'zg.getProductInfo.start' );
    };


    GetProductInfo.prototype.__onComplete = function () {
        // >>> FIX: chiude lo stesso loader che __onBeforeSend ha mostrato,
        // invece di decidere di nuovo in base a paginationType (che resta
        // 'infinite' anche per il primo caricamento, lasciando altrimenti
        // #page-loader sempre visibile)
        if ( this._usingFullPageLoader ) {
            $( '#page-loader' ).fadeOut(300);
        } else {
            this.__hideInlineLoader();
        }
        $( document ).trigger( 'zg.getProductInfo.complete' );
    };


    GetProductInfo.prototype.__onError = function ( response ) {
        if ( DEBUG ) {
            console.log( 'GetProductInfo - ERROR', response );
        }

        if ( this.options.emptyContainer ) {
            this.$container.empty();
        }

        if ( this.options.errorTemplate ) {
            this.$container.append(
                handlebarsTemplates.render( this.options.errorTemplate, response || {} )
            );
        } else {
            $( document ).trigger( 'zg-error', [{
                message: root.JS_TRANSLATIONS.genericErrorMsg
            }] );
        }

        this._isInfiniteRequestPending = false;

        // >>> FIX: stessa logica di __onComplete, per non lasciare il
        // page-loader appeso in caso di errore sulla prima richiesta
        if ( this._usingFullPageLoader ) {
            $( '#page-loader' ).fadeOut(300);
        } else {
            this.__hideInlineLoader();
        }

        $( document ).trigger( 'zg.getProductInfo.error' );
    };


    GetProductInfo.prototype.__updateURL = function( response ){
        var request, availableKeys = {}, appliedFilters = {};

        if( response ) {
            availableKeys = _.allKeys(response.filters);
        }

        if( this.request.filterBy ) {
            appliedFilters = this.request.filterBy;

            if (appliedFilters.hasOwnProperty('search')) {
                var searchedValue = this.request.filterBy.search.value;
                delete appliedFilters.search;
                _.extend(appliedFilters, {search: searchedValue});
            }
        }

        if( response && response.pagination.page && this.options.paginationType == 'pages'){
            appliedFilters = _.extend( { page: response.pagination.page }, appliedFilters );
        }

        if( response && response.pagination.page && this.options.updateUrlByPage && this.options.paginationType !== 'infinite'){
            appliedFilters = _.extend( { page: response.pagination.page }, appliedFilters );
        }

        if( this.request.sortBy ){
            appliedFilters = _.extend( { sortBy: this.request.sortBy }, appliedFilters );
        }
        if( this.request.filterBy && this.request.filterBy.search ){
            appliedFilters = _.extend( { search: this.request.filterBy.search.value }, appliedFilters );
        }

        request = {
            applied:   appliedFilters,
            available: this.options.validParameters,
            data:      { categoryId: + (this.request.categoryId) }
        };

        $.uriMgr( request );
    };


    GetProductInfo.prototype.updateCounters = function () {
        this.$counter
            .hide()
            .text( this.total )
            .fadeIn();

        switch( this.options.paginationType ){
            case 'pages':
                this.__paginatePages();
                break;
            case 'pager':
                this.__paginatePager();
                break;
            case 'load-more':
                this.__paginateLoadMore();
                break;
            case 'infinite':
                // >>> PATCH: ad ogni scroll dell'utente si carica subito la
                // pagina successiva, senza aspettare di avvicinarsi al fondo
                this.options.emptyContainer = false;
                this.__setupInfiniteObserver();
                break;
        }
    };

    // >>> PATCH: infinite scroll "eager" - un solo listener su scroll/wheel,
    // niente calcoli di distanza dal fondo. Ogni volta che l'utente scrolla
    // (rotellina, trackpad, touch, barra di scroll) e non c'e' gia' una
    // richiesta in corso, si carica subito la pagina successiva.
    var INFINITE_SCROLL_NS = '.zg.pagination.eager';

    GetProductInfo.prototype.__setupInfiniteObserver = function () {
        var that = this;

        $( window ).off( 'scroll' + INFINITE_SCROLL_NS + ' wheel' + INFINITE_SCROLL_NS + ' touchmove' + INFINITE_SCROLL_NS );

        if ( this.page >= (this.totalPages - 1) ) {
            // ultima pagina raggiunta: nessun altro caricamento necessario
            return;
        }

        $( window ).on( 'scroll' + INFINITE_SCROLL_NS + ' wheel' + INFINITE_SCROLL_NS + ' touchmove' + INFINITE_SCROLL_NS, function () {
            if ( that._isInfiniteRequestPending ) {
                return;
            }
            if ( that.page >= (that.totalPages - 1) ) {
                $( window ).off( 'scroll' + INFINITE_SCROLL_NS + ' wheel' + INFINITE_SCROLL_NS + ' touchmove' + INFINITE_SCROLL_NS );
                return;
            }
            that.__triggerNextPage();
        } );
    };

    GetProductInfo.prototype.__triggerNextPage = function () {
        this._isInfiniteRequestPending = true;
        this.request.page = this.page + 1;

        if ( this.request.page < this.totalPages ) {
            this.getInfo();
        } else {
            this._isInfiniteRequestPending = false;
        }
    };

    GetProductInfo.prototype.__showInlineLoader = function () {
        if ( this.$inlineLoader && this.$inlineLoader.length ) {
            this.$inlineLoader.removeClass( 'hidden' ).fadeIn( 150 );
        }
    };

    GetProductInfo.prototype.__hideInlineLoader = function () {
        if ( this.$inlineLoader && this.$inlineLoader.length ) {
            this.$inlineLoader.fadeOut( 150, function () {
                $( this ).addClass( 'hidden' );
            } );
        }
    };

    GetProductInfo.prototype.__paginatePager = function (){
        if(this.totalPages <= 1 ){
            this.$pagerControls.addClass( 'hidden' );
            return;
        }

        this.options.emptyContainer = true;

        this.$pagerPrev.data( 'page', this.page - 1 );
        this.$pagerPrev[0].parentElement.classList.remove('disabled');
        this.$pagerNext.data( 'page', this.page + 1 );
        this.$pagerNext[0].parentElement.classList.remove('disabled');

        if ( this.page === 0 ) {
            this.$pagerPrev[0].parentElement.classList.add('disabled');
        }

        if ( this.page >= parseInt(this.totalPages - 1) ) {
            this.$pagerNext[0].parentElement.classList.add('disabled');
        }

        this.$paginationCurrent.text( parseInt(this.page + 1)  + '/' +  this.totalPages);
        this.$pagerControls.removeClass( 'hidden' );

    };

    GetProductInfo.prototype.__paginateLoadMore = function (){
        let itemCount = this.pageSize;

        this.options.emptyContainer = true;

        if( this.page > 0 ){
            itemCount = this.pageSize * ( this.page + 1 );
        }
        this.$paginationLoadMoreBtn.find( 'button').data( 'page', ( this.page + 1 ) );
        this.$paginationXYelements.find( '[data-zg-role="count-x"]' ).html( itemCount );
        this.$paginationXYelements.find( '[data-zg-role="count-y"]' ).html( this.total );

        if( itemCount < this.total ) {
            this.$paginationLoadMoreBtn.removeClass( 'hidden' );
        }
        else {
            this.$paginationLoadMoreBtn.addClass( 'hidden' );
        }
    };


    GetProductInfo.prototype.__paginatePages = function (){
        var lessPages, morePages = false, diffPages;
        var counter = 2;
        var i = 1;

        if( this.totalPages >= 1 ) {
            this.$paginationControls.removeClass( 'hidden' );

            if ( this.totalPages === 1 ) {
                counter = 2;
            }
            else if ( this.page > 1 && this.page < 4 ) {
                counter = this.page + 1
            }
            else if ( this.page >= 4 ) {
                i = this.page - 1;
                counter = i + 2;
            }

            if ( this.page >= 4 ) {
                lessPages = true;
            }
            if ( this.totalPages > 3 && ( this.totalPages - this.page > 2 )) {
                morePages = true;
            }
            if( this.totalPages - this.page < 3 ){
                diffPages = this.totalPages - 3;
                if( diffPages > 0 ) {
                    i = diffPages;
                }
                counter = this.totalPages - 1;
            }

            if ( this.page === 0 ) {
                this.$paginationPrev.prop( 'disabled', true );
                this.$paginationNext.prop( 'disabled', false );
            }
            else {
                this.$paginationPrev.prop( 'disabled', false );
                this.$paginationPrev.data( 'page', ( this.page - 1 ));

                if ( this.page === ( this.totalPages - 1 ) || this.page === 1 && this.totalPages === 1 ) {
                    this.$paginationNext.prop( 'disabled', true );
                }
                else {
                    this.$paginationNext.prop( 'disabled', false );
                }
            }
            this.$paginationNext.data( 'page', ( this.page + 1 ) );

            this.$paginationNumberPages.empty();

            this.__renderPaginationItem( 0 );

            if ( lessPages ) {
                this.__renderPaginationItem( null, null, true );
            }

            while( i <= counter ){
                this.__renderPaginationItem( i );
                i++;
            }

            if ( morePages ) {
                this.__renderPaginationItem( null, null, true );
                this.__renderPaginationItem( this.totalPages - 1 );
            }

        }
        else{
            this.$paginationControls.addClass( 'hidden' );
        }
    };

    GetProductInfo.prototype.__renderPaginationItem = function ( i, isDisabled, isDot ) {
        var $item, data = {};

        if( isDot ) {
            data['isDot'] = true;
        }
        else {
            data['i'] = i + 1;
            data['pageTo'] = i;
            data['isActive'] = false;
            if (this.page == (i)) {
                data.isActive = true;
            }
            if (isDisabled) {
                data['isDisabled'] = true;
            }
        }

        $item = $(handlebarsTemplates.render('pagination-item', data));
        this.$paginationNumberPages.append($item);
    };


    GetProductInfo.prototype.__createFilters = function ( filters, products, url ) {
        var $containers = [],
            containers = {
                defaultContainer: []
            };

        _.each( filters, function ( filter, filterId ) {
            this.__renderSingleFilter( filterId, filter, containers );
        }, this );

        if ( this.options.enableSearch ) {
            this.__renderSingleFilter( 'search', {
                value: "",
                typeahead: ""
            }, containers );
        }

        if ( this.options.enableReset ) {
            this.__renderSingleFilter( 'reset', null, containers );
        }

        _.each( containers, function ( filtersArray, containerSelector ) {
            var $container = this.$initialContainer;

            if ( this.options.sortFilters ) {
                filtersArray.sort( zg_sortElements( {
                    attr:              this.options.sortFiltersBy,
                    pattern:           _.isArray( this.options.sortFilters ) ? this.options.sortFilters : null,
                    avoidNumbersOnTop: true
                } ) );
            }

            if ( containerSelector !== 'defaultContainer' ) {
                $container = $(containerSelector);
            }

            this.$filterContainer.html( filtersArray ).removeClass('loading');

        }, this );

        if( this.options.isFirstLoad ) {
            _.each(this.request.filterBy, function (filter_values, filter_Key) {
                $( '[data-filter="' + filter_Key + '"][data-zg-action="reset"]' ).removeClass('active')

                _.each(filter_values, function (value) {
                    $( '[data-filter="' + filter_Key + '"][data-value="' + value + '"]' ).addClass('active')
                });
            });

            if( this.request.sortBy ){
                $( '[data-zg-role="pagination-sort-bck"]li.active' ).removeClass( 'active' );
                $( '[data-sort-by=' + this.request.sortBy + ']' ).parent().addClass( 'active' );
            }
            this.options.isFirstLoad = false;
        }

        $( document ).trigger( 'filters.renderFilters', [this.$element, this.filters, this.appliedFilters, this.$filterContainer] );
    };


    GetProductInfo.prototype.__renderSingleFilter = function ( filterId, filterObject, containers ) {
        var $item,
            containerId,
            filterOptions;

        if ( filterId && containers ) {
            if ( filterObject ) {
                filterObject.id = filterId;
            }

            filterOptions = this.__getFilterOptions( filterId );
            containerId = filterOptions.container || 'defaultContainer';

            $item = this.createFilter( filterId, filterObject, filterOptions.template );

            if ( $item ) {
                if ( !containers[containerId] ) {
                    containers[containerId] = [];
                }
                $.merge( containers[containerId], $item );

            } else {
                console.warn( 'Invalid filter', filterId );
            }
        }
    };


    GetProductInfo.prototype.__getFilterOptions = function ( filterId ) {
        var options = {};

        if ( this.options.filtersOptions && _.isObject( this.options.filtersOptions[filterId] ) ) {
            options = this.options.filtersOptions[filterId];
        }

        return options;
    };


    GetProductInfo.prototype.createFilter = function ( filterId, data, template ) {
        var $filter;

        switch ( filterId ) {
            case 'price':
                if ( (data.max - data.min) >= this.options.sliderMinDiff ) {
                    $filter = $( handlebarsTemplates.render( template || this.options.templateFilterSlider, data ) );
                } else if ( DEBUG ) {
                    console.info( 'price filter not created: Price difference under minimum limit' );
                }
                break;

            case 'reset':
                $filter = $( handlebarsTemplates.render( template || this.options.templateFilterReset ) );
                break;

            case 'search':
                $filter = $( handlebarsTemplates.render( template || this.options.templateFilterSearch, data ) );
                break;

            default:
                $filter = $( handlebarsTemplates.render( template || this.options.templateFilterList, data ) );
        }

        if ( $filter ) {
            $filter.data( this.options.sortFiltersBy, filterId );
        }

        return $filter;
    };


    GetProductInfo.prototype.resetFilters = function () {
        var $searchInput = $( '[data-zg-role="filter-search"]' );

        this.request.page = 0;
        this.options.emptyContainer = true;
        this.__emtpyContainer();

        if( $searchInput.val(). length) {
            $searchInput.val( '' );
        }

        delete this.request.filterBy;
        if( this.request.sortBy ) {
            delete this.request.sortBy;

            this.$elementSorting.removeClass( 'active' );
            var liElements = this.$elementSorting.parent().children();
            liElements.first().addClass( 'active' );
        }


        $( '.content-filter' ).find( 'a.active' ).removeClass( 'active' );
        $( '.content-filter' ).find( 'a:first-child' ).addClass( 'active' );

        this.__updatePriceSlider( this.options.sliderMin, this.options.sliderMax );

        $('body,html').animate({
            scrollTop: 0
        }, 500);

        this.getInfo();
    };


    GetProductInfo.prototype.__updatePriceSlider = function( minValue, maxValue ) {
        var slider = document.getElementById('slider');

        if( slider ) {
            var $filterMin = $('[data-zg-role="label-filter-min"]');
            var $filterMax = $('[data-zg-role="label-filter-max"]');

            slider.noUiSlider.updateOptions({start: [minValue, maxValue]});
            $filterMin.html(window.renderPrice(minValue));
            $filterMax.html(window.renderPrice(maxValue));
        }
    }


    GetProductInfo.prototype.__processProducts = function ( products ) {
        _.each( products || [], function ( product ) {
            product.type = this.options.type;

            if ( this.request.categoryId ) {
                product.currentCategory = this.request.categoryId;
            }
        }, this );
    };


    GetProductInfo.prototype.__processRequest = function ( config ) {
        var param;
        var value;
        var request = {};

        for ( param in DEFAULT_REQUEST ) {
            if (
                DEFAULT_REQUEST.hasOwnProperty( param ) &&
                config.hasOwnProperty( param ) &&
                ( config[param] || config[param] === 0 )
            ) {
                value = config[param];

                if ( _.isString( value ) ) {
                    value = value.split( ',' );
                }

                request[param] = value;
            }
        }

        this.request = request;
    };


    GetProductInfo.prototype.updateOptions = function ( options ) {
        this.options = _.extendOwn( {}, DEFAULTS, this.options, options || {} );
        this.request = _.extendOwn( {}, DEFAULT_REQUEST, this.request, options || {} );
        this.__processRequest(this.request || {});
    };


    GetProductInfo.prototype.__setEventHandlers = function () {
        var that = this;

        this.$filterContainer.off( '.zg.filters.applyFilter' );

        this.$filterContainer.on( 'click.zg.filters.applyFilter', this.options.filterElement, function ( e ) {
            var $this, data;

            $this = $( this );

            if ( !$this.is( 'select' ) && !$this.is( 'option' ) && !$this.is( 'input' ) ) {
                e.preventDefault();

                data = $this.data();
                var filter = {};
                var filterValue = [];
                var isFilterActive = $this.hasClass( 'active' );
                var dataValue = data.value;
                if( dataValue ) {
                    dataValue = data.value.toString();
                }

                if( data.zgAction == 'reset' ){
                    $( this ).closest( '.content-filter' ).find( 'a.active' ).removeClass( 'active' );
                    $( this ).addClass( 'active' );
                    if( _.has(that.request.filterBy, data.filter) ){
                        delete that.request.filterBy[ data.filter ];
                    }
                }
                else {
                    that.request.page = 0;
                    if ( _.has(that.request.filterBy, data.filter) && dataValue  ) {
                        filterValue = that.request.filterBy[ data.filter ];
                    } else {
                        $( this ).closest( '.content-filter' ).find( 'a.active' ).removeClass( 'active' );
                    }

                    var indexOfFilterValue = filterValue.indexOf( dataValue );

                    if ( indexOfFilterValue == -1 && !isFilterActive ) {
                        filterValue.push( dataValue );
                        filter[ data.filter ] = filterValue.sort();
                        $( this ).addClass( 'active' );
                    }
                    else {
                        filterValue.splice( indexOfFilterValue, 1 );
                        $( this ).removeClass( 'active' );

                        if ( filterValue.length == 0 ) {
                            $( this ).closest( '.content-filter' ).find( 'a:first-child' ).addClass( 'active' );
                            delete that.request.filterBy[ data.filter ];
                        }
                    }
                }

                if( !that.request.filterBy ) {
                    that.request.filterBy = filter;
                }
                else if( _.isEmpty( that.request.filterBy ) ){
                    delete that.request.filterBy;
                }
                else{
                    that.request.filterBy = _.extend( that.request.filterBy, filter );
                }

                if( that.options.paginationType == "infinite" ){
                    that.request.page = 0;
                }

                $('body,html').animate({
                    scrollTop: 0
                }, 500);

                that.options.emptyContainer = true;
                that.__emtpyContainer();

                that.getInfo( false, true );
            }
        } );


        this.$element.on( 'click.zg.filters.searchButton', this.options.searchButton, function() {
            var searched = $( '[data-zg-role="filter-search"]' ).val();
            var searched_parsed = zgParseString( searched, true );
            if ( searched_parsed !== (that.request.search || [] ) ) {
                searched = { value: searched_parsed, fields: [ 'name', 'option', 'mainoption']};
                if( !that.request.filterBy ) {
                    that.request.filterBy = {};
                    that.request.filterBy.search = searched;
                }
                else {
                    that.request.filterBy.search = searched;
                }
                that.request.page = 0;
                that.options.emptyContainer = true;
                that.__emtpyContainer();

                $('body,html').animate({
                    scrollTop: 0
                }, 500);

                that.getInfo( false, true );
            }
        } );

        this.$element.on( 'click.zg.filters.resetFilters', this.options.resetFilters, function ( e ) {
            e.preventDefault();
            that.resetFilters();
        } );


        this.$element.on( 'click.zg.pagination.sort', this.options.elementSorting + ' [data-sort-by]', function ( e ) {
            var $this = $( this );
            var sortByValue = $this.data( 'sort-by' );

            e.preventDefault();

            that.$element.find( that.options.elementSorting ).removeClass( 'active' );
            $this.closest( that.options.elementSorting ).addClass( 'active' );
            if( sortByValue ){
                that.request.sortBy = sortByValue;
            }
            else {
                delete that.request.sortBy;
            }

            that.request.page = 0;
            that.options.emptyContainer = true;
            that.__emtpyContainer();

            that.getInfo( false, true );

        } );


        this.$element.on( 'click.zg.pagination.go.to', this.options.paginationGoTo, function( e ){
            var pageNumber = $( this ).data( 'page' );
            that.request.page = pageNumber;

            if( that.options.paginationType == 'pages' ) {
                that.options.emptyContainer = true;
                that.__emtpyContainer();

                $('body,html').animate({
                    scrollTop: 0
                }, 500);
            }
            else if( that.options.paginationType == 'load-more' ){
                that.options.emptyContainer = false;
            }

            if( pageNumber != -1 ) {
                that.getInfo( false, true );
            }
        } );

        $( document ).one( 'filters.renderFilters', function(){
            var minValue, maxValue, startMinValue, startMaxValue;
            var slider = document.getElementById( 'slider' );
            var $filterMin = $( '[data-zg-role="label-filter-min"]' );
            var $filterMax = $( '[data-zg-role="label-filter-max"]' );

            if( slider ){
                minValue = Math.round( Number( slider.dataset.min ) );
                maxValue = Math.round( Number( slider.dataset.max ) );
                that.options.sliderMin = minValue;
                that.options.sliderMax = maxValue;

                startMinValue = (that.request.filterBy && that.request.filterBy["price-min"]) ? that.request.filterBy["price-min"] : minValue;
                startMaxValue = (that.request.filterBy && that.request.filterBy["price-max"]) ? that.request.filterBy["price-max"] : maxValue;

                $filterMin.html( window.renderPrice( startMinValue ) );
                $filterMax.html( window.renderPrice( startMaxValue ) );

                noUiSlider.create(slider, {
                    start: [ startMinValue, startMaxValue ],
                    connect: true,
                    tooltips: false,
                    step: that.options.sliderStep,
                    range: {
                        'min': Number( slider.dataset.min ),
                        'max': maxValue
                    },
                });

                slider.noUiSlider.on('change', function( values ){
                    var minApplied = Math.round( values[0] );
                    var maxApplied = Math.round( values[1] );

                    if( !that.request.filterBy ){
                        that.request.filterBy = {};
                    }
                    if( minApplied > minValue ){
                        that.request.filterBy['price-min'] = minApplied;
                        $filterMin.html( window.renderPrice( minApplied ) );
                    }
                    else{
                        delete that.request.filterBy['price-min'];
                    }

                    if( maxApplied < maxValue ){
                        that.request.filterBy['price-max'] = maxApplied;
                        $filterMax.html( window.renderPrice( maxApplied ) );
                    }
                    else{
                        delete that.request.filterBy['price-max'];
                    }

                    that.options.emptyContainer = true;
                    that.request.page = 0;
                    that.__emtpyContainer();

                    that.getInfo( false, true );
                });
            }

        });

    };


    // GETPRODUCTINFO DEFINITION
    // ============================

    function Plugin ( option, updateOptions ) {
        return this.each( function () {
            var $this   = $( this );
            var data    = $this.data( 'zg.getProductInfo' );
            var options = $.extend( {}, root.ZG_CONFIG || {}, $this.data(), typeof option === 'object' && option );

            if ( !data ) {
                $this.data( 'zg.getProductInfo', (data = new GetProductInfo( this, options )) );
            } else if ( updateOptions && typeof option === 'object' ) {
                data.updateOptions( option );
            }

            data.getInfo( true );
        } );
    }

    $.fn.getProductInfo             = Plugin;
    $.fn.getProductInfo.Constructor = GetProductInfo;


    // GETPRODUCTINFO DATA-API
    // ===================

    $( function () {
        $( SELECTOR ).each( function () {
            Plugin.call( $( this ) );
        } );
    } );

}.call( this, jQuery, _ ));